// frontend/src/pages/DevToolsPage.jsx
//
// Route: /devtools
// Access: admin role (from AdminRoute in App.js) + email in Firestore devtools whitelist

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/apiClient';
import './DevToolsPage.css';

// ─── tiny helpers ─────────────────────────────────────────────────────────────

function Badge({ type, children }) {
  const colours = {
    normal: '#3b82f6',
    school: '#8b5cf6',
    emergency: '#f59e0b',
    unknown: '#6b7280',
  };
  return (
    <span
      style={{
        background: colours[type] || colours.unknown,
        color: '#fff',
        borderRadius: 4,
        padding: '1px 7px',
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}
    >
      {children}
    </span>
  );
}

function Ksh(n) {
  return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function Toast({ msg, type, onClose }) {
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, [msg, onClose]);
  if (!msg) return null;
  return (
    <div className={`dt-toast dt-toast--${type}`}>
      <span>{msg}</span>
      <button onClick={onClose}>✕</button>
    </div>
  );
}

function ConfirmModal({ open, title, body, onConfirm, onCancel, danger = true }) {
  if (!open) return null;
  return (
    <div className="dt-modal-overlay">
      <div className="dt-modal">
        <h3 className={danger ? 'dt-modal__title--danger' : ''}>{title}</h3>
        <p className="dt-modal__body">{body}</p>
        <div className="dt-modal__actions">
          <button className="dt-btn dt-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className={`dt-btn ${danger ? 'dt-btn--danger' : 'dt-btn--primary'}`} onClick={onConfirm}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SECTION: Loan card ───────────────────────────────────────────────────────

function LoanCard({ loan, onAmountSaved, onDeleted, showToast }) {
  const [editing, setEditing] = useState(false);
  const [newAmount, setNewAmount] = useState(String(loan.originalAmount));
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function saveAmount() {
    const val = Number(newAmount);
    if (isNaN(val) || val <= 0) { showToast('Invalid amount', 'error'); return; }
    setSaving(true);
    try {
      const res = await api.patch(`/devtools/loans/${loan.id}/amount`, { originalAmount: val });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      showToast('Loan amount updated ✓', 'success');
      onAmountSaved(loan.id, val);
      setEditing(false);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function doDelete() {
    setDeleting(true);
    try {
      const res = await api.delete(`/devtools/loans/${loan.id}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      showToast(data.message, 'success');
      onDeleted(loan.id);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  return (
    <div className="dt-card dt-card--loan">
      <div className="dt-card__row">
        <Badge type={loan.loanType}>{loan.loanType}</Badge>
        <span className="dt-card__date">{loan.disbursementDate || '—'}</span>
        <span className="dt-card__sub">Duration: {loan.duration} mo</span>
        <span className="dt-card__sub">mPP: Ksh {Ksh(loan.monthlyPrincipalPayment)}</span>
      </div>
      <div className="dt-card__row dt-card__row--main">
        <div>
          <span className="dt-card__label">Original Amount</span>
          {editing ? (
            <div className="dt-inline-edit">
              <input
                type="number"
                value={newAmount}
                onChange={(e) => setNewAmount(e.target.value)}
                className="dt-input dt-input--sm"
                autoFocus
              />
              <button className="dt-btn dt-btn--primary dt-btn--sm" onClick={saveAmount} disabled={saving}>
                {saving ? '…' : 'Save'}
              </button>
              <button className="dt-btn dt-btn--ghost dt-btn--sm" onClick={() => { setEditing(false); setNewAmount(String(loan.originalAmount)); }}>
                Cancel
              </button>
            </div>
          ) : (
            <div className="dt-inline-edit">
              <span className="dt-card__amount">Ksh {Ksh(loan.originalAmount)}</span>
              <button className="dt-btn dt-btn--ghost dt-btn--sm" onClick={() => setEditing(true)}>Edit Amount</button>
            </div>
          )}
        </div>
        <button
          className="dt-btn dt-btn--danger dt-btn--sm"
          onClick={() => setConfirmDelete(true)}
          disabled={deleting}
        >
          {deleting ? 'Deleting…' : '🗑 Delete Loan'}
        </button>
      </div>
      <div className="dt-card__id">Loan ID: {loan.id}</div>

      <ConfirmModal
        open={confirmDelete}
        title="Delete this loan?"
        body={`This will permanently delete the ${loan.loanType} loan of Ksh ${Ksh(loan.originalAmount)} (disbursed ${loan.disbursementDate}), all its payment records, and its application fee document. This cannot be undone.`}
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

// ─── SECTION: Fee card ────────────────────────────────────────────────────────

function FeeCard({ fee, onSaved, onDeleted, showToast }) {
  const [editing, setEditing] = useState(false);
  const [newAmount, setNewAmount] = useState(String(fee.amount));
  const [newDate, setNewDate] = useState(fee.date || '');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function saveFee() {
    const payload = {};
    if (newAmount !== String(fee.amount)) {
      const v = Number(newAmount);
      if (isNaN(v) || v < 0) { showToast('Invalid amount', 'error'); return; }
      payload.amount = v;
    }
    if (newDate && newDate !== fee.date) payload.date = newDate;
    if (!Object.keys(payload).length) { setEditing(false); return; }
    setSaving(true);
    try {
      const res = await api.patch(`/devtools/fees/${fee.id}`, payload);
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      showToast('Fee updated ✓', 'success');
      onSaved(fee.id, payload);
      setEditing(false);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function doDelete() {
    try {
      const res = await api.delete(`/devtools/fees/${fee.id}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      showToast('Fee deleted ✓', 'success');
      onDeleted(fee.id);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setConfirmDelete(false);
    }
  }

  return (
    <div className="dt-card dt-card--fee">
      <div className="dt-card__row">
        <Badge type={fee.loanType}>{fee.loanType || 'fee'}</Badge>
        <span className="dt-card__sub">{fee.type}</span>
      </div>

      {editing ? (
        <div className="dt-card__edit-block">
          <label className="dt-label">Amount (Ksh)</label>
          <input type="number" className="dt-input" value={newAmount} onChange={(e) => setNewAmount(e.target.value)} />
          <label className="dt-label">Date (YYYY-MM-DD)</label>
          <input type="text" className="dt-input" value={newDate} onChange={(e) => setNewDate(e.target.value)} placeholder="2026-01-01" />
          <div className="dt-card__row" style={{ marginTop: 8 }}>
            <button className="dt-btn dt-btn--primary dt-btn--sm" onClick={saveFee} disabled={saving}>{saving ? '…' : 'Save'}</button>
            <button className="dt-btn dt-btn--ghost dt-btn--sm" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="dt-card__row dt-card__row--main">
          <div>
            <span className="dt-card__amount">Ksh {Ksh(fee.amount)}</span>
            <span className="dt-card__date" style={{ marginLeft: 12 }}>{fee.date || '—'}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="dt-btn dt-btn--ghost dt-btn--sm" onClick={() => setEditing(true)}>Edit</button>
            <button className="dt-btn dt-btn--danger dt-btn--sm" onClick={() => setConfirmDelete(true)}>🗑 Delete</button>
          </div>
        </div>
      )}
      <div className="dt-card__id">Fee ID: {fee.id} {fee.loanId ? `| Loan: ${fee.loanId}` : ''}</div>

      <ConfirmModal
        open={confirmDelete}
        title="Delete this fee?"
        body={`Permanently delete this ${fee.type} of Ksh ${Ksh(fee.amount)} dated ${fee.date}?`}
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

// ─── SECTION: Share record card ───────────────────────────────────────────────

function ShareCard({ share, memberCode, onDeleted, showToast }) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function doDelete() {
    try {
      const res = await api.delete(`/devtools/members/${memberCode}/shares/${share.id}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      showToast('Share record deleted ✓', 'success');
      onDeleted(share.id);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setConfirmDelete(false);
    }
  }

  return (
    <div className="dt-card" style={{ borderLeft: `3px solid ${share.isPlan ? '#1d9e75' : '#f59e0b'}` }}>
      <div className="dt-card__row dt-card__row--main">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
            background: share.isPlan ? '#14532d' : '#7c2d12',
            color: share.isPlan ? '#86efac' : '#fcd34d',
          }}>
            {share.isPlan ? '📅 Plan' : '💰 Deposit'}
          </span>
          <span className="dt-card__date">{share.date || '—'}</span>
        </div>
        <button className="dt-btn dt-btn--danger dt-btn--sm" onClick={() => setConfirmDelete(true)}>🗑 Delete</button>
      </div>

      <div className="dt-card__row">
        <div style={{ minWidth: 140 }}>
          <span className="dt-card__label">Amount</span>
          <span className="dt-card__amount" style={{ color: share.isPlan ? '#86efac' : '#fcd34d' }}>
            Ksh {Ksh(share.amount)}
          </span>
        </div>
        <div style={{ minWidth: 160 }}>
          <span className="dt-card__label">Running Total</span>
          <span className="dt-card__sub" style={{ fontSize: 14 }}>Ksh {Ksh(share.runningTotal)}</span>
        </div>
        <div style={{ flex: 1 }}>
          <span className="dt-card__label">Description</span>
          <span className="dt-card__sub">{share.description || '—'}</span>
        </div>
      </div>

      {(share.editedBy || share.paymentMethod || share.reference) && (
        <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>
          {share.paymentMethod && <span>Method: {share.paymentMethod}</span>}
          {share.reference && <span> · Ref: {share.reference}</span>}
          {share.editedBy && <span> · By: {share.editedByName || share.editedBy}</span>}
        </div>
      )}
      <div className="dt-card__id">Share ID: {share.id}</div>

      <ConfirmModal
        open={confirmDelete}
        title={share.isPlan ? '⚠ Delete plan contribution?' : 'Delete deposit record?'}
        body={share.isPlan
          ? `This is a managed plan contribution of Ksh ${Ksh(share.amount)} dated ${share.date}. Deleting it will reduce the member's balance. The plan engine may re-create this on the next Save Month. Proceed?`
          : `Permanently delete this deposit of Ksh ${Ksh(share.amount)} dated ${share.date} (${share.description})?`
        }
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

// ─── SECTION: Member-level danger operations ──────────────────────────────────

function MemberDangerZone({ member, showToast, onMemberDeleted, onSharesDeleted, onMemberResetExit }) {
  const [newId, setNewId] = useState('');
  const [confirmRename, setConfirmRename] = useState(false);
  const [confirmShares, setConfirmShares] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmResetExit, setConfirmResetExit] = useState(false);
  const [busy, setBusy] = useState('');

  async function doResetExit() {
    setBusy('resetExit');
    try {
      const res = await api.post(`/devtools/members/${member.memberCode}/reset-exit`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      showToast(data.message, 'success');
      onMemberResetExit();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setBusy('');
      setConfirmResetExit(false);
    }
  }

  async function doRename() {
    if (!newId.trim()) { showToast('Enter new member ID', 'error'); return; }
    setBusy('rename');
    try {
      const res = await api.post(`/devtools/members/${member.memberCode}/rename-id`, { newMemberId: newId.trim() });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      showToast(`Renamed to ${newId} ✓. Reload to search again.`, 'success');
      setNewId('');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setBusy('');
      setConfirmRename(false);
    }
  }

  async function doDeleteShares() {
    setBusy('shares');
    try {
      const res = await api.delete(`/devtools/members/${member.memberCode}/all-shares`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      showToast(data.message, 'success');
      onSharesDeleted();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setBusy('');
      setConfirmShares(false);
    }
  }

  async function doDeleteComplete() {
    setBusy('delete');
    try {
      const res = await api.delete(`/devtools/members/${member.memberCode}/complete`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      showToast(data.message, 'success');
      onMemberDeleted();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setBusy('');
      setConfirmDelete(false);
    }
  }

  return (
    <div className="dt-danger-zone">
      <h4 className="dt-danger-zone__title">⚠ Member-Level Operations</h4>

      {/* Reset Member Exit — only shown when member is Left */}
      {member.status === 'Left' && (
        <div className="dt-danger-row">
          <div>
            <div className="dt-danger-row__label">Reset Member Exit</div>
            <div className="dt-danger-row__desc">
              Restores this member to Active, clears all exit fields, deletes the exit share records, and re-enables their login. Use to re-run the Exit wizard after a mistake.
            </div>
          </div>
          <button
            className="dt-btn dt-btn--warning dt-btn--sm"
            onClick={() => setConfirmResetExit(true)}
            disabled={busy === 'resetExit'}
          >
            {busy === 'resetExit' ? 'Resetting…' : '↩ Reset Exit'}
          </button>
        </div>
      )}

      {/* Rename ID */}
      <div className="dt-danger-row">
        <div>
          <div className="dt-danger-row__label">Rename Member ID</div>
          <div className="dt-danger-row__desc">Updates the memberId across all collections (loans, fees, shares, etc.)</div>
        </div>
        <div className="dt-inline-edit">
          <input
            className="dt-input dt-input--sm"
            placeholder="New ID e.g. S1007"
            value={newId}
            onChange={(e) => setNewId(e.target.value)}
          />
          <button
            className="dt-btn dt-btn--warning dt-btn--sm"
            onClick={() => { if (newId.trim()) setConfirmRename(true); else showToast('Enter new ID first', 'error'); }}
            disabled={busy === 'rename'}
          >
            {busy === 'rename' ? 'Renaming…' : 'Rename'}
          </button>
        </div>
      </div>

      {/* Delete all shares */}
      <div className="dt-danger-row">
        <div>
          <div className="dt-danger-row__label">Delete All Shares</div>
          <div className="dt-danger-row__desc">Removes every share record and clears sharesPlan from the member document.</div>
        </div>
        <button
          className="dt-btn dt-btn--danger dt-btn--sm"
          onClick={() => setConfirmShares(true)}
          disabled={busy === 'shares'}
        >
          {busy === 'shares' ? 'Deleting…' : '🗑 Delete All Shares'}
        </button>
      </div>

      {/* Complete delete */}
      <div className="dt-danger-row dt-danger-row--critical">
        <div>
          <div className="dt-danger-row__label">Delete Member Completely</div>
          <div className="dt-danger-row__desc">
            Permanently removes the member document <strong>plus</strong> all loans, loan payments,
            fees, shares, transactions, monthly deductions, and any linked user account.
            <br /><strong>This is irreversible.</strong>
          </div>
        </div>
        <button
          className="dt-btn dt-btn--danger dt-btn--sm"
          onClick={() => setConfirmDelete(true)}
          disabled={busy === 'delete'}
        >
          {busy === 'delete' ? 'Deleting…' : '💣 Delete Completely'}
        </button>
      </div>

      <ConfirmModal
        open={confirmRename}
        title={`Rename ${member.memberCode} → ${newId}?`}
        body={`This will update the memberId from "${member.memberCode}" to "${newId}" across the members, users, loans, loan_payments, fees, shares, transactions, and monthly_deductions collections.`}
        onConfirm={doRename}
        onCancel={() => setConfirmRename(false)}
        danger={false}
      />
      <ConfirmModal
        open={confirmShares}
        title="Delete ALL shares for this member?"
        body={`All share contribution records for ${member.name} (${member.memberCode}) will be permanently deleted and sharesPlan will be cleared.`}
        onConfirm={doDeleteShares}
        onCancel={() => setConfirmShares(false)}
      />
      <ConfirmModal
        open={confirmDelete}
        title={`Completely delete ${member.name}?`}
        body={`This will permanently delete ${member.name} (${member.memberCode}) AND all their loans, loan payments, fees, shares, transactions, monthly deductions, and linked user account. Type-check: is this really the member you mean to remove?`}
        onConfirm={doDeleteComplete}
        onCancel={() => setConfirmDelete(false)}
      />
      <ConfirmModal
        open={confirmResetExit}
        title={`Reset exit for ${member.name}?`}
        body={`This will restore ${member.name} (${member.memberCode}) to Active status, clear all exit fields, delete the exit share records (and any share capital transfer), and re-enable their login. You can then re-run the Exit wizard.`}
        onConfirm={doResetExit}
        onCancel={() => setConfirmResetExit(false)}
        danger={false}
      />
    </div>
  );
}

// ─── SECTION: DevTools Access Manager ────────────────────────────────────────

const PROTECTED_EMAIL = 'jeprosservices@gmail.com';

function AccessManager({ showToast }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState('');

  // Get current user email from localStorage
  const currentUserEmail = (() => {
    try { return (JSON.parse(localStorage.getItem('hopeSaccoUser') || '{}')).email?.toLowerCase() || ''; }
    catch { return ''; }
  })();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/devtools/settings');
      const data = await res.json();
      if (data.success) setEmails(data.allowedEmails || []);
    } catch (e) {
      showToast('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  async function addEmail() {
    if (!newEmail.includes('@')) { showToast('Enter a valid email', 'error'); return; }
    setBusy('add');
    try {
      const res = await api.post('/devtools/settings/add-user', { email: newEmail.trim() });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      showToast(`${newEmail} added ✓`, 'success');
      setNewEmail('');
      await load();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setBusy('');
    }
  }

  async function removeEmail(email) {
    setBusy(email);
    try {
      const res = await api.delete('/devtools/settings/remove-user', { body: JSON.stringify({ email }) });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      showToast(`${email} removed ✓`, 'success');
      await load();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="dt-section">
      <h3 className="dt-section__title">🔑 DevTools Access</h3>
      <p className="dt-section__desc">
        Users listed here (who also have the <code>admin</code> role) can access this page.
      </p>
      {loading ? (
        <p className="dt-muted">Loading…</p>
      ) : (
        <ul className="dt-email-list">
          {emails.length === 0 && <li className="dt-muted">No users on whitelist.</li>}
          {emails.map((email) => {
            const isProtected = email.toLowerCase() === PROTECTED_EMAIL;
            const isSelf = email.toLowerCase() === currentUserEmail;
            const canRemove = !isProtected && !isSelf;
            return (
              <li key={email} className="dt-email-list__item">
                <span>
                  {email}
                  {isProtected && <span style={{ marginLeft: 8, fontSize: 10, color: '#f59e0b', fontWeight: 600 }}>🔒 </span>}
                  {isSelf && !isProtected && <span style={{ marginLeft: 8, fontSize: 10, color: '#60a5fa', fontWeight: 600 }}>YOU</span>}
                </span>
                {canRemove ? (
                  <button
                    className="dt-btn dt-btn--danger dt-btn--xs"
                    onClick={() => removeEmail(email)}
                    disabled={busy === email}
                  >
                    {busy === email ? '…' : 'Remove'}
                  </button>
                ) : (
                  <span style={{ fontSize: 11, color: '#475569', fontStyle: 'italic' }}>
                    {isProtected ? 'Cannot remove' : 'Cannot remove self'}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <div className="dt-inline-edit" style={{ marginTop: 16 }}>
        <input
          className="dt-input"
          type="email"
          placeholder="email@example.com"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addEmail()}
        />
        <button className="dt-btn dt-btn--primary" onClick={addEmail} disabled={busy === 'add'}>
          {busy === 'add' ? 'Adding…' : '+ Add'}
        </button>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function DevToolsPage() {
  const navigate = useNavigate();

  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  const [searchCode, setSearchCode] = useState('');
  const [searching, setSearching] = useState(false);
  const [member, setMember] = useState(null);
  const [loans, setLoans] = useState([]);
  const [fees, setFees] = useState([]);
  const [shares, setShares] = useState([]);
  const [memberGone, setMemberGone] = useState(false);

  const [activeTab, setActiveTab] = useState('loans');

  const [toast, setToast] = useState({ msg: '', type: 'success' });

  const showToast = useCallback((msg, type = 'success') => setToast({ msg, type }), []);

  // ── check devtools access on mount ──────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/devtools/access');
        if (res.ok) {
          setHasAccess(true);
        } else {
          setHasAccess(false);
        }
      } catch {
        setHasAccess(false);
      } finally {
        setAccessChecked(true);
      }
    })();
  }, []);

  async function handleSearch(e) {
    e.preventDefault();
    if (!searchCode.trim()) return;
    setSearching(true);
    setMember(null);
    setLoans([]);
    setFees([]);
    setShares([]);
    setMemberGone(false);
    try {
      const res = await api.post('/devtools/lookup-member', { memberCode: searchCode.trim() });
      const data = await res.json();
      if (!data.success) { showToast(data.error, 'error'); return; }
      setMember(data.member);
      setLoans(data.loans);
      setFees(data.fees);
      setShares(data.shares || []);
      setActiveTab('loans');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSearching(false);
    }
  }

  function handleAmountSaved(loanId, newAmt) {
    setLoans((prev) => prev.map((l) => l.id === loanId ? { ...l, originalAmount: newAmt } : l));
  }

  function handleLoanDeleted(loanId) {
    setLoans((prev) => prev.filter((l) => l.id !== loanId));
    setFees((prev) => prev.filter((f) => f.loanId !== loanId));
  }

  function handleFeeEdited(feeId, updates) {
    setFees((prev) => prev.map((f) => f.id === feeId ? { ...f, ...updates } : f));
  }

  function handleFeeDeleted(feeId) {
    setFees((prev) => prev.filter((f) => f.id !== feeId));
  }

  function handleShareDeleted(shareId) {
    setShares((prev) => {
      // Recalculate running totals after deletion
      const remaining = prev.filter((s) => s.id !== shareId);
      let running = 0;
      return remaining.map((s) => { running += s.amount; return { ...s, runningTotal: running }; });
    });
  }

  // ── render ──────────────────────────────────────────────────────────────────

  if (!accessChecked) {
    return (
      <div className="dt-root">
        <div className="dt-checking">Verifying DevTools access…</div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="dt-root">
        <div className="dt-denied">
          <div className="dt-denied__icon">🔒</div>
          <h2>DevTools Access Denied</h2>
          <p>Your account is not on the DevTools whitelist. Contact the system owner.</p>
          <button className="dt-btn dt-btn--ghost" onClick={() => navigate('/admin/dashboard')}>
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dt-root">
      <Toast msg={toast.msg} type={toast.type} onClose={() => setToast({ msg: '', type: 'success' })} />

      {/* ── Header ── */}
      <div className="dt-header">
        <div className="dt-header__left">
          <button className="dt-back" onClick={() => navigate('/admin/dashboard')}>← Dashboard</button>
          <div>
            <h1 className="dt-header__title">⚙ DevTools</h1>
            <p className="dt-header__sub">Hope SACCO · Direct data operations · Use with caution</p>
          </div>
        </div>
        <div className="dt-warning-pill">⚠ Live Production Data</div>
      </div>

      <div className="dt-body">

        {/* ── Left sidebar ── */}
        <div className="dt-sidebar">
          <div className="dt-sidebar__section">
            <div className="dt-sidebar__label">Member Operations</div>
            <button className={`dt-sidebar__item ${activeTab === 'loans' ? 'active' : ''}`} onClick={() => setActiveTab('loans')}>💳 Loans</button>
            <button className={`dt-sidebar__item ${activeTab === 'fees' ? 'active' : ''}`} onClick={() => setActiveTab('fees')}>🧾 Fees</button>
            <button className={`dt-sidebar__item ${activeTab === 'shares' ? 'active' : ''}`} onClick={() => setActiveTab('shares')}>💰 Shares</button>
            <button className={`dt-sidebar__item ${activeTab === 'danger' ? 'active' : ''}`} onClick={() => setActiveTab('danger')}>⚠ Danger Zone</button>
          </div>
          <div className="dt-sidebar__section">
            <div className="dt-sidebar__label">System</div>
            <button className={`dt-sidebar__item ${activeTab === 'access' ? 'active' : ''}`} onClick={() => setActiveTab('access')}>🔑 DevTools Access</button>
          </div>
        </div>

        {/* ── Main panel ── */}
        <div className="dt-main">

          {/* Member search — always visible when not on access tab */}
          {activeTab !== 'access' && (
            <div className="dt-search-bar">
              <form onSubmit={handleSearch} className="dt-search-form">
                <input
                  className="dt-input dt-input--lg"
                  placeholder="Member code e.g. S1006"
                  value={searchCode}
                  onChange={(e) => setSearchCode(e.target.value)}
                />
                <button className="dt-btn dt-btn--primary" type="submit" disabled={searching}>
                  {searching ? 'Looking up…' : 'Load Member'}
                </button>
              </form>

              {member && !memberGone && (
                <div className="dt-member-pill">
                  <span className="dt-member-pill__code">{member.memberCode}</span>
                  <span className="dt-member-pill__name">{member.name}</span>
                  <span className={`dt-member-pill__status dt-member-pill__status--${(member.status || '').toLowerCase()}`}>
                    {member.status || 'Active'}
                  </span>
                  <span className="dt-member-pill__sub">Firestore: {member.docId}</span>
                </div>
              )}
              {memberGone && (
                <div className="dt-member-pill dt-member-pill--gone">
                  Member was deleted. Search again or go back to Dashboard.
                </div>
              )}
            </div>
          )}

          {/* ── TAB: Loans ── */}
          {activeTab === 'loans' && (
            <div className="dt-section">
              <h3 className="dt-section__title">💳 Loans</h3>
              {!member && <p className="dt-muted">Search for a member above to load their loans.</p>}
              {member && loans.length === 0 && <p className="dt-muted">No loans found for this member.</p>}
              {loans.map((loan) => (
                <LoanCard
                  key={loan.id}
                  loan={loan}
                  onAmountSaved={handleAmountSaved}
                  onDeleted={handleLoanDeleted}
                  showToast={showToast}
                />
              ))}
            </div>
          )}

          {/* ── TAB: Fees ── */}
          {activeTab === 'fees' && (
            <div className="dt-section">
              <h3 className="dt-section__title">🧾 Application Fees</h3>
              {!member && <p className="dt-muted">Search for a member above to load their fees.</p>}
              {member && fees.length === 0 && <p className="dt-muted">No fee records found for this member.</p>}
              {fees.map((fee) => (
                <FeeCard
                  key={fee.id}
                  fee={fee}
                  onSaved={handleFeeEdited}
                  onDeleted={handleFeeDeleted}
                  showToast={showToast}
                />
              ))}
            </div>
          )}

          {/* ── TAB: Shares ── */}
          {activeTab === 'shares' && (
            <div className="dt-section">
              <h3 className="dt-section__title">💰 Share Records</h3>
              <p className="dt-section__desc">
                Green border = plan contribution (auto-managed). Amber border = deposit (manually entered).
                All records are deletable here — plan records may be re-created on the next Save Month.
              </p>
              {!member && <p className="dt-muted">Search for a member above to load their shares.</p>}
              {member && shares.length === 0 && <p className="dt-muted">No share records found for this member.</p>}
              {member && shares.length > 0 && (
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                  {shares.length} record(s) · Total: Ksh {Ksh(shares[shares.length - 1]?.runningTotal || 0)}
                </div>
              )}
              {shares.map((share) => (
                <ShareCard
                  key={share.id}
                  share={share}
                  memberCode={member?.memberCode}
                  onDeleted={handleShareDeleted}
                  showToast={showToast}
                />
              ))}
            </div>
          )}

          {/* ── TAB: Danger Zone ── */}
          {activeTab === 'danger' && (
            <div className="dt-section">
              <h3 className="dt-section__title">⚠ Danger Zone</h3>
              {!member && <p className="dt-muted">Search for a member above first.</p>}
              {member && !memberGone && (
                <MemberDangerZone
                  member={member}
                  showToast={showToast}
                  onSharesDeleted={() => showToast('All shares deleted ✓', 'success')}
                  onMemberResetExit={() => setMember((prev) => prev ? { ...prev, status: 'Active' } : prev)}
                  onMemberDeleted={() => {
                    setMemberGone(true);
                    setMember(null);
                    setLoans([]);
                    setFees([]);
                  }}
                />
              )}
            </div>
          )}

          {/* ── TAB: DevTools Access ── */}
          {activeTab === 'access' && (
            <AccessManager showToast={showToast} />
          )}
        </div>
      </div>
    </div>
  );
}
